import React, { useState, useEffect } from "react";
import "./AdminDashboardApprove.css";

function AdminDashboardApprove() {
  const [venues, setVenues] = useState([]);
  const [selectedVenues, setSelectedVenues] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedVenueId, setSelectedVenueId] = useState(null);
  const [imageFile, setImageFile] = useState(null);

  // Fetch unverified venues from backend
  useEffect(() => {
    const fetchUnverifiedVenues = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_FUTSAL_DIRECTORY_API_URL}/api/admin/unverifiedVenues`);

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error('Failed to fetch venues: ', errorText);
        }

        const responseData = await response.text();
        const data = JSON.parse(responseData);
        setVenues(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchUnverifiedVenues();
  }, []);

  // Handle checkbox selection
  const handleCheckboxChange = (venueId) => {
    setSelectedVenues(prev => {
      const newSelection = prev.includes(venueId)
        ? prev.filter(id => id !== venueId)
        : [...prev, venueId];
      return newSelection;
    });
  };

  // Handle approval of selected venues
  const handleApprove = async () => {
    if (selectedVenues.length === 0) {
      return;
    }

    try {
      setIsLoading(true);
      const response = await fetch(`${process.env.REACT_APP_FUTSAL_DIRECTORY_API_URL}/api/admin/approve-venues`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ venueIds: selectedVenues }),
      });

      if (!response.ok) {
        throw new Error('Approval failed');
      }

      setVenues(prev => prev.filter(venue => !selectedVenues.includes(venue.id)));
      setSelectedVenues([]);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle image upload modal open
  const handleUploadClick = (venueId) => {
    setSelectedVenueId(venueId);
    setShowUploadModal(true);
  };

  // Handle file selection
  const handleFileChange = (e) => {
    setImageFile(e.target.files[0]);
  };

  // Handle image upload
  const handleImageUpload = async () => {
    if (!imageFile || !selectedVenueId) return;

    try {
      setUploadingImage(selectedVenueId);
      
      const formData = new FormData();
      formData.append('image', imageFile);
      formData.append('venueId', selectedVenueId);

      const response = await fetch(`${process.env.REACT_APP_FUTSAL_DIRECTORY_API_URL}/api/admin/upload-venue-image`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Image upload failed');
      }

      const updatedVenue = await response.json();
      
      // Update the venue in state
      setVenues(prev =>
        prev.map(venue =>
          venue.id === updatedVenue.id ? updatedVenue : venue
        )
      );

      // Reset upload state
      setShowUploadModal(false);
      setImageFile(null);
      setSelectedVenueId(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setUploadingImage(null);
    }
  };

  // Format amenities for display
  const formatAmenities = (venue) => {
    const amenities = [];
    if (venue.has_parking) amenities.push("Parking");
    if (venue.has_cafe) amenities.push("Cafe");
    if (venue.has_seating) amenities.push("Seating");
    if (venue.has_vending) amenities.push("Vending");
    if (venue.has_disability_access) amenities.push("Disability Access");
    if (venue.has_changing_rooms) amenities.push("Changing Rooms");
    if (venue.has_showers) amenities.push("Showers");
    if (venue.has_toilets) amenities.push("Toilets");
    if (venue.has_gym) amenities.push("Gym");
    if (venue.has_fitness_studio) amenities.push("Fitness Studio");
    if (venue.has_community_room) amenities.push("Community Room");
    return amenities.join(", ");
  };

  if (isLoading) {
    return <div className="loading">Loading venues...</div>;
  }

  if (error) {
    return <div className="error">Error: {error}</div>;
  }

  return (
    <div className="form_approval_page">
      {/* Upload Image Modal */}
      {showUploadModal && (
        <div className="modal-overlay">
          <div className="upload-modal">
            <h3>Upload Image for Venue</h3>
            <input 
              type="file" 
              accept="image/*" 
              onChange={handleFileChange}
              disabled={uploadingImage}
            />
            <div className="modal-actions">
              <button 
                onClick={handleImageUpload}
                disabled={!imageFile || uploadingImage}
                className="upload-button"
              >
                {uploadingImage ? 'Uploading...' : 'Upload Image'}
              </button>
              <button 
                onClick={() => {
                  setShowUploadModal(false);
                  setImageFile(null);
                }}
                className="cancel-button"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="approval-header">
        <h2>Venues Pending Approval</h2>
        <button
          onClick={handleApprove}
          disabled={selectedVenues.length === 0}
          className="approve-button"
        >
          Approve Selected ({selectedVenues.length})
        </button>
      </div>

      <div className="table-container">
        <table className="venues-table">
          <thead>
            <tr>
              <th style={{ width: '50px' }}>Select</th>
              <th>Venue Name</th>
              <th>Description</th>
              <th>Location</th>
              <th>Contact</th>
              <th>Amenities</th>
              <th>Courts</th>
              <th>Images</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {venues.length > 0 ? (
              venues.map(venue => (
                <tr
                  key={venue.id}
                  className={selectedVenues.includes(venue.id) ? 'selected-row' : ''}
                >
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedVenues.includes(venue.id)}
                      onChange={() => handleCheckboxChange(venue.id)}
                    />
                  </td>
                  <td>{venue.name}</td>
                  <td className="description-cell">
                    {venue.description || 'N/A'}
                  </td>
                  <td>
                    {venue.address_line1}<br />
                    {venue.address_line2 && <>{venue.address_line2}<br /></>}
                    {venue.city}, {venue.state} {venue.postcode}<br />
                    {venue.country}
                  </td>
                  <td>
                    {venue.contact_email}<br />
                    {venue.contact_phone}<br />
                    {venue.contact_website && (
                      <a href={venue.contact_website} target="_blank" rel="noopener noreferrer">
                        Website
                      </a>
                    )}
                  </td>
                  <td className="amenities-cell">
                    {formatAmenities(venue) || 'None'}
                  </td>
                  <td>
                    {venue.courts?.length || 0} courts
                    {venue.courts?.length > 0 && (
                      <div className="court-details">
                        {venue.courts.map(court => (
                          <div key={`${venue.id}-${court.court_number}`}>
                            Court {court.court_number}: {court.surface_type}
                          </div>
                        ))}
                      </div>
                    )}
                  </td>
                  <td>
                    {venue.images?.length || 0} images
                    {venue.images?.length > 0 && (
                      <div className="image-preview">
                        {venue.images.find(img => img.is_thumbnail) ? (
                          <img 
                            src={venue.images.find(img => img.is_thumbnail).url} 
                            alt="Thumbnail" 
                            className="thumbnail-image"
                          />
                        ) : (
                          <img 
                            src={venue.images[0].url} 
                            alt="Venue" 
                            className="thumbnail-image"
                          />
                        )}
                      </div>
                    )}
                  </td>
                  <td>
                    {new Date(venue.created_at).toLocaleDateString()}
                  </td>
                  <td>
                    <button 
                      onClick={() => handleUploadClick(venue.id)}
                      className="upload-image-button"
                    >
                      Add Image
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="10" className="no-venues">
                  No venues pending approval
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default AdminDashboardApprove;